import React, { lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useAuth, normalizeUserType } from 'Utils/Hooks/useAuth';
import AppShell, { isPendingPartner } from 'Shell/AppShell';

/** Admin pages */
const AdminDashboardPage = lazy(() => import('Views/Admin/Dashboard'));
const AdminProductsPage = lazy(() => import('Views/Admin/Products'));
const AdminShortcodePage = lazy(() => import('Views/Admin/Shortcode'));
const AdminUserManagerPage = lazy(() => import('Views/Admin/UserManager'));
const AdminConfigurationPage = lazy(() => import('Views/Admin/Configuration'));
const AdminPartnerShortcodePage = lazy(() => import('Views/Admin/Partner'));
const AdminEmailReportPage = lazy(() => import('Views/Admin/EmailReport'));
const AdminReportPage = lazy(() => import('Views/Admin/Report'));
const AdminApiCheckerPage = lazy(() => import('Views/Admin/ApiChecker'));
const AdminPartnerCheckerPage = lazy(() => import('Views/Admin/PartnerChecker'));
const AdminLogTracerPage = lazy(() => import('Views/Admin/LogTracer'));
const AdminAccountsPage = lazy(() => import('Views/Admin/Accounts'));
const AdminServicesPage = lazy(() => import('Views/Admin/Services'));
const AdminAggregatorDocsPage = lazy(() => import('Views/Admin/AggregatorDocuments'));
const AdminOpsPage = lazy(() => import('Views/Admin/Ops'));
const AdminRetentionPage = lazy(() => import('Views/Admin/Retention'));
const AdminGcmsPage = lazy(() => import('Views/Admin/Gcms'));
const AdminGcmsRateCardPage = lazy(() => import('Views/Admin/Gcms/RateCard'));

/** Partner pages */
const PartnerDashboardPage = lazy(() => import('Views/Partner/Dashboard'));
const PartnerProductPage = lazy(() => import('Views/Partner/Products'));
const PartnerMessagePage = lazy(() => import('Views/Partner/Message'));
const PartnerReportPage = lazy(() => import('Views/Partner/Report'));
const PartnerNotificationPage = lazy(() => import('Views/Partner/Notification'));
const PartnerVerificationPage = lazy(() => import('Views/Partner/Verification'));
const PartnerServicesPage = lazy(() => import('Views/Partner/Services'));
const PartnerSandboxPage = lazy(() => import('Views/Partner/Sandbox'));
const PartnerDailySummaryPage = lazy(() => import('Views/Partner/DailySummary'));
const PartnerGcmsPage = lazy(() => import('Views/Partner/Gcms'));
const PartnerAnalyticsPage = lazy(() => import('Views/Partner/Analytics'));
const HelpPage = lazy(() => import('Help/HelpCenter'));
const AccountBlockedPage = lazy(() => import('Views/Partner/Verification/AccountBlocked'));

const PrivateRoutes = () => {
  const { currentAdmin } = useAuth();
  const role = normalizeUserType(currentAdmin?.user_type);
  const status = currentAdmin?.account_status;

  // A partner whose company checks are still open can only finish verification and try the sandbox.
  if (role === 'USER' && isPendingPartner(currentAdmin)) {
    if (status !== 'pending_verification') {
      return (
        <Routes>
          <Route path='*' element={<AccountBlockedPage />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route path='partner/verification' element={<PartnerVerificationPage />} />
          <Route path='partner/sandbox' element={<PartnerSandboxPage />} />
          <Route path='partner/help' element={<HelpPage />} />
          <Route path='partner/help/:articleId' element={<HelpPage />} />
          <Route path='*' element={<Navigate to='/partner/verification' replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        {role === 'ADMIN' && (
          <>
            <Route path='auth/*' element={<Navigate to='/dashboard/home' />} />
            <Route path='dashboard' element={<Navigate to='/dashboard/home' replace />} />
            <Route path='dashboard/home' element={<AdminDashboardPage />} />
            <Route path='dashboard/product/*' element={<AdminProductsPage />} />
            <Route path='dashboard/configuration/*' element={<AdminConfigurationPage />} />
            <Route path='dashboard/shortcode/*' element={<AdminShortcodePage />} />
            <Route path='dashboard/usermanager/*' element={<AdminUserManagerPage />} />
            <Route path='dashboard/partner-shortcode' element={<AdminPartnerShortcodePage />} />
            <Route path='dashboard/email-report' element={<AdminEmailReportPage />} />
            <Route path='dashboard/report/*' element={<AdminReportPage />} />
            <Route path='dashboard/api-checker' element={<AdminApiCheckerPage />} />
            <Route path='dashboard/partner-checker' element={<AdminPartnerCheckerPage />} />
            <Route path='dashboard/log-tracer' element={<AdminLogTracerPage />} />
            <Route path='dashboard/accounts/*' element={<AdminAccountsPage />} />
            <Route path='dashboard/services/*' element={<AdminServicesPage />} />
            <Route path='dashboard/aggregator-documents' element={<AdminAggregatorDocsPage />} />
            <Route path='dashboard/ops/*' element={<AdminOpsPage />} />
            <Route path='dashboard/retention' element={<AdminRetentionPage />} />
            <Route path='dashboard/gcms/*' element={<AdminGcmsPage />} />
            <Route path='dashboard/settings/gcms-rate-card' element={<AdminGcmsRateCardPage />} />
            <Route path='dashboard/help' element={<HelpPage />} />
            <Route path='dashboard/help/:articleId' element={<HelpPage />} />
          </>
        )}

        {role === 'USER' && (
          <>
            <Route path='auth/*' element={<Navigate to='/partner/home' />} />
            <Route path='partner' element={<Navigate to='/partner/home' replace />} />
            <Route path='partner/home' element={<PartnerDashboardPage />} />
            <Route path='partner/product/*' element={<PartnerProductPage />} />
            <Route path='partner/messages/send-message' element={<PartnerMessagePage />} />
            <Route path='partner/report/*' element={<PartnerReportPage />} />
            <Route path='partner/notification/*' element={<PartnerNotificationPage />} />
            <Route path='partner/verification' element={<PartnerVerificationPage />} />
            <Route path='partner/services/*' element={<PartnerServicesPage />} />
            <Route path='partner/sandbox' element={<PartnerSandboxPage />} />
            <Route path='partner/daily-summary' element={<PartnerDailySummaryPage />} />
            <Route path='partner/gcms/*' element={<PartnerGcmsPage />} />
            {/* Always routed; the page explains when analytics is not switched on (menu item hidden). */}
            <Route path='partner/analytics' element={<PartnerAnalyticsPage />} />
            <Route path='partner/help' element={<HelpPage />} />
            <Route path='partner/help/:articleId' element={<HelpPage />} />
          </>
        )}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  );
};

export default PrivateRoutes;
